import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './SearchableSelect.css';

const DIACRITICS_RE = new RegExp('[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g');
const normalize = (s) =>
  String(s ?? '').toLowerCase().normalize('NFD').replace(DIACRITICS_RE, '');

const toOption = (o) => (typeof o === 'string' ? { value: o, label: o } : o);

const filterOptions = (options, query) => {
  if (!query) return options;
  const q = normalize(query);
  return options.filter(o => normalize(o.label).includes(q));
};

// Native <select> replacement with a search box at the top of the dropdown —
// used wherever the option list can get long enough that scrolling to find
// one entry (brand, model, ...) is slower than typing it.
export default function SearchableSelect({
  value,
  onChange,
  options,
  groups,
  placeholder,
  searchPlaceholder,
  disabled,
  icon,
  label,
  variant = 'plain', // 'plain' (boxed input look) | 'segment' (icon + caption, hero bar look)
  className = '',
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const flatGroups = groups
    ? groups.map(g => ({ label: g.label, options: g.options.map(toOption) }))
    : [{ label: null, options: (options || []).map(toOption) }];

  const selected = flatGroups.flatMap(g => g.options).find(o => String(o.value) === String(value));

  // Panel is portaled to <body> (position: fixed) so it can escape any
  // overflow:hidden / sticky ancestor (search bar card, sidebar, ...).
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const id = setTimeout(() => searchRef.current?.focus(), 0);

    const updatePos = () => {
      const r = rootRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    updatePos();

    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  const select = (v) => { onChange(v); setOpen(false); };

  const visibleGroups = flatGroups
    .map(g => ({ ...g, options: filterOptions(g.options, query) }))
    .filter(g => g.options.length > 0);
  const hasResults = visibleGroups.length > 0;

  return (
    <div
      ref={rootRef}
      className={`ss-select ss-select--${variant} ${disabled ? 'ss-select--disabled' : ''} ${className}`}
    >
      <button
        type="button"
        className="ss-select__trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        {icon && <span className="sc-segment__icon">{icon}</span>}
        {variant === 'segment' ? (
          <span className="sc-segment__body">
            <span className="sc-segment__label">{label}</span>
            <span className="ss-select__value">{selected ? selected.label : placeholder}</span>
          </span>
        ) : (
          <span className="ss-select__value">{selected ? selected.label : placeholder}</span>
        )}
        <ExpandMoreIcon className="ss-select__chevron" sx={{ fontSize: 18, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="ss-select__panel"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: Math.max(pos.width, 220) }}
        >
          <div className="ss-select__search-wrap">
            <SearchIcon sx={{ fontSize: 16 }} className="ss-select__search-icon" />
            <input
              ref={searchRef}
              className="ss-select__search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
              placeholder={searchPlaceholder || t('search')}
            />
          </div>
          <div className="ss-select__list" role="listbox">
            {!query && (
              <div
                role="option"
                aria-selected={!value}
                className={`ss-select__option ${!value ? 'ss-select__option--active' : ''}`}
                onClick={() => select('')}
              >
                {placeholder}
              </div>
            )}
            {visibleGroups.map((g, gi) => (
              <div key={g.label || gi}>
                {g.label && <div className="ss-select__group-label">{g.label}</div>}
                {g.options.map(o => (
                  <div
                    key={o.value}
                    role="option"
                    aria-selected={String(o.value) === String(value)}
                    className={`ss-select__option ${String(o.value) === String(value) ? 'ss-select__option--active' : ''}`}
                    onClick={() => select(o.value)}
                  >
                    {o.label}
                  </div>
                ))}
              </div>
            ))}
            {!hasResults && <div className="ss-select__empty">{t('noResults')}</div>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
